import { store } from '@grafana/data';
import { reportInteraction } from '@grafana/runtime';
import { FlagKeys, getFeatureFlagClient } from '@grafana/runtime/internal';

/**
 * How the open extension sidebar is laid out: `docked` as a column next to the page, or `floating` over the
 * page at its bottom centre. The same container and extension instance render in both, so switching
 * never remounts the extension.
 */
export type ExtensionSidebarPresentation = 'docked' | 'floating';

/** The sidebar floats until the user picks a layout; the pick then holds on every page. */
const DEFAULT_PRESENTATION: ExtensionSidebarPresentation = 'floating';

const PRESENTATION_LOCAL_STORAGE_KEY = 'grafana.navigation.extensionSidebarPresentation';
/**
 * Where the layout was remembered per kind of page (dashboards and notebooks, everything else). Read once, so a
 * user who floated it on either kind keeps floating; removed with the first pick.
 */
const LEGACY_PRESENTATION_LOCAL_STORAGE_KEY = 'grafana.navigation.extensionSidebarPresentationBySurface';

/**
 * Plugins that draw their own floating surface and drag handle, and can dock again through the restricted
 * `extensionSidebar` API. Any other sidebar plugin always docks.
 */
const FLOATING_EXTENSION_SIDEBAR_PLUGINS = ['grafana-assistant-app'];

/**
 * The layout the user asked for, through the restricted `extensionSidebar` API. Floating only applies while
 * `grafana.extensionSidebarFloating` is on; `ExtensionSidebarContextProvider` resolves the effective layout.
 *
 * Module state on purpose: there is one extension sidebar per page, and plugins reach it only through the
 * restricted API, not an app event any plugin could publish. Loaded lazily from local storage.
 */
let requested: ExtensionSidebarPresentation | undefined;
const listeners = new Set<() => void>();

function isPresentation(value: unknown): value is ExtensionSidebarPresentation {
  return value === 'docked' || value === 'floating';
}

function loadRequested(): ExtensionSidebarPresentation {
  const stored = store.get(PRESENTATION_LOCAL_STORAGE_KEY);
  if (isPresentation(stored)) {
    return stored;
  }
  const legacy: unknown = store.getObject(LEGACY_PRESENTATION_LOCAL_STORAGE_KEY, {});
  const legacyPicks = legacy && typeof legacy === 'object' ? Object.values(legacy).filter(isPresentation) : [];
  // Floated on either kind of page: keep floating. Docked on every kind the user picked for: stay docked.
  if (legacyPicks.length > 0 && legacyPicks.every((pick) => pick === 'docked')) {
    return 'docked';
  }
  return DEFAULT_PRESENTATION;
}

/** Whether the extension sidebar may float. */
export function canFloatExtensionSidebar(): boolean {
  return getFeatureFlagClient().getBooleanValue(FlagKeys.GrafanaExtensionSidebarFloating, false);
}

/** Sets the requested layout, for every page. Floating is ignored while `canFloatExtensionSidebar()` is false. */
export function setExtensionSidebarPresentation(presentation: ExtensionSidebarPresentation): void {
  if (presentation === 'floating' && !canFloatExtensionSidebar()) {
    return;
  }
  const from = getRequestedExtensionSidebarPresentation();
  if (presentation === from) {
    return;
  }
  requested = presentation;
  store.set(PRESENTATION_LOCAL_STORAGE_KEY, presentation);
  store.delete(LEGACY_PRESENTATION_LOCAL_STORAGE_KEY);
  listeners.forEach((listener) => listener());
  reportInteraction('grafana_extension_sidebar_presentation_changed', { from, to: presentation });
}

/**
 * The layout `pluginId`'s sidebar shows on the current page, given the requested one: floating only while the
 * flag allows it and only for a plugin that supports it.
 */
export function resolveExtensionSidebarPresentation(
  requestedPresentation: ExtensionSidebarPresentation,
  canFloat: boolean,
  pluginId: string | undefined
): ExtensionSidebarPresentation {
  return canFloat &&
    requestedPresentation === 'floating' &&
    pluginId !== undefined &&
    FLOATING_EXTENSION_SIDEBAR_PLUGINS.includes(pluginId)
    ? 'floating'
    : 'docked';
}

/** The layout `pluginId`'s sidebar shows on the current page. */
export function getEffectiveExtensionSidebarPresentation(pluginId: string | undefined): ExtensionSidebarPresentation {
  return resolveExtensionSidebarPresentation(
    getRequestedExtensionSidebarPresentation(),
    canFloatExtensionSidebar(),
    pluginId
  );
}

/** The requested layout, before the feature flag applies. */
export function getRequestedExtensionSidebarPresentation(): ExtensionSidebarPresentation {
  requested ??= loadRequested();
  return requested;
}

/** For `useSyncExternalStore`. */
export function subscribeToExtensionSidebarPresentation(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Holds on the floating sidebar: while any is held, a floating sidebar stays mounted but hidden. A page that
 * offers its own Assistant prompt holds it until the prompt is submitted, so the page does not show two inputs
 * for the same thing. Any request to open the sidebar releases every hold, so the user opening it, or the page
 * handing its prompt over, shows it. Module state for the same reason as the layout: one sidebar per page.
 */
const floatingHolds = new Set<symbol>();

/** Holds the floating sidebar hidden; returns the release. */
export function holdFloatingExtensionSidebar(): () => void {
  const hold = Symbol('floating-extension-sidebar-hold');
  floatingHolds.add(hold);
  listeners.forEach((listener) => listener());
  return () => {
    if (floatingHolds.delete(hold)) {
      listeners.forEach((listener) => listener());
    }
  };
}

/** Releases every hold, when something asks to open the sidebar. */
export function releaseFloatingExtensionSidebarHolds(): void {
  if (floatingHolds.size > 0) {
    floatingHolds.clear();
    listeners.forEach((listener) => listener());
  }
}

/** Whether a floating sidebar is held hidden. Subscribe with `subscribeToExtensionSidebarPresentation`. */
export function isFloatingExtensionSidebarHeld(): boolean {
  return floatingHolds.size > 0;
}

/** @internal Resets the layout between tests; `keepStorage` stands for a new page load. */
export function resetExtensionSidebarPresentationForTests({ keepStorage = false } = {}): void {
  requested = undefined;
  if (!keepStorage) {
    store.delete(PRESENTATION_LOCAL_STORAGE_KEY);
    store.delete(LEGACY_PRESENTATION_LOCAL_STORAGE_KEY);
  }
  floatingHolds.clear();
  listeners.clear();
}
